import React, { useState, useEffect } from 'react';
import { IState, ReduxMap } from '../../redux/redux';
import { store } from '../../redux/oldStore';
import { ServerResponse } from '../../utils/Server';
import { toast } from 'react-toastify';
import { ArchiveResultsSearchOperator, IArchiveResult, IArchiveResultsSearchRequest, IArchiveSearchInfo, IArchiveSearchResult } from './EventResultTypes';
import Button from '../_Core/Button';
import Modal from '../Modal/Modal';
import { Tab, TabList, TabPanel, Tabs } from 'react-tabs';
import { Selectable } from '../../utils/CommonTypes/CommonTypes';
import { DatePart, FormatDate, FormatPhoneNumber } from '../../utils/Tools';
import Select from 'react-select';
import Icon, { IconType } from '../Icon/Icon';

interface IProps extends IState {}

export const ArchiveSearch = (props: IProps) => {
  const [info, setInfo] = useState<IArchiveSearchInfo>();
  const [search, setSearch] = useState<IArchiveResultsSearchRequest>({ search: [], operator: ArchiveResultsSearchOperator.And });
  const [data, setData] = useState<IArchiveSearchResult>();
  const [selectedResult, setSelectedResult] = useState<IArchiveResult>();
  const [showSelectedResult, setShowSelectedResult] = useState<boolean>(false);
  const [showSearchModal, setShowSearchModal] = useState<boolean>(true);
  const [archiveYears, setArchiveYears] = useState<Selectable[]>([]);
  const [grades, setGrades] = useState<Selectable[]>([]);
  const [stringOperators, setStringOperators] = useState<Selectable[]>();
  const [numberOperators, setNumberOperators] = useState<Selectable[]>();
  const [orderByOptions, setOrderByOptions] = useState<Selectable[]>([]);
  const [showResultsTabSchool, setShowResultsTabSchool] = useState<boolean>(true);
  const [showResultsTabProject, setShowResultsTabProject] = useState<boolean>(true);
  const [showResultsTabAwards, setShowResultsTabAwards] = useState<boolean>(true);

  useEffect(() => {
    init();
  }, []);

  useEffect(() => {
    if (selectedResult) {
      setShowResultsTabSchool(!!(selectedResult.School || selectedResult.TeacherFirstName || selectedResult.TeacherLastName || selectedResult.Grade));
      setShowResultsTabProject(
        !!(
          selectedResult.IsTeam !== undefined ||
          selectedResult.Title ||
          selectedResult.Category_name ||
          selectedResult.ProjectId ||
          selectedResult.ProjectTagsCsv ||
          selectedResult.ProjectDescription ||
          selectedResult.EntryTypeName ||
          selectedResult.ProjectStatus ||
          selectedResult.ProjectPlan ||
          selectedResult.ProjectAbstract
        )
      );
      setShowResultsTabAwards(!!(selectedResult.Award || selectedResult.SpecialAward || selectedResult.SpecialAwardSubType));
    }
  }, [selectedResult]);

  useEffect(() => {
    const years: Selectable[] = [];
    years.push({ label: 'Select All', value: -1 });
    if (info && info.events) {
      info.events.forEach((e) => {
        years.push({ label: `${DatePart(e.awardDate, 'year')}`, value: e.id });
      });
    }
    setArchiveYears(years);

    const grds: Selectable[] = [];
    grds.push({ label: 'Select All', value: -1 });
    grds.push({ label: 'High School (9-12)', value: -100 });
    grds.push({ label: 'Jr High School (7-8)', value: -101 });
    grds.push({ label: 'Middle School (6-8)', value: -102 });
    grds.push({ label: 'Elementary (K-5)', value: -103 });

    for (let i = 0; i <= 12; i++) {
      //grds.push({ label: i.toString(), value: i });
      if (i === 0) grds.push({ label: 'Kindergarten', value: i });
      else if (i === 1) grds.push({ label: '1st Grade', value: i });
      else if (i === 2) grds.push({ label: '2nd Grade', value: i });
      else if (i === 3) grds.push({ label: '3rd Grade', value: i });
      else grds.push({ label: `${i}th Grade`, value: i });
    }
    setGrades(grds);

    const strOps: Selectable[] = [
      { label: 'Equals', value: ArchiveResultsSearchOperator.Equal },
      { label: 'Contains', value: ArchiveResultsSearchOperator.Contains },
      { label: 'Starts With', value: ArchiveResultsSearchOperator.StartsWith },
      { label: 'Ends With', value: ArchiveResultsSearchOperator.EndsWith },
    ];
    setStringOperators(strOps);

    const numOps: Selectable[] = [
      { label: 'Equals', value: ArchiveResultsSearchOperator.Equal },
      { label: 'Less Than', value: ArchiveResultsSearchOperator.LessThan },
      { label: 'Greater Than', value: ArchiveResultsSearchOperator.GreaterThan },
    ];
    setNumberOperators(numOps);

    const orderBys: Selectable[] = [
      { label: 'Last Name', value: 'LastName' },
      { label: 'First Name', value: 'FirstName' },
      { label: 'Entry Title', value: 'Title' },
      { label: 'Category', value: 'Category_name' },
      { label: 'School', value: 'School' },
      { label: 'Teacher Last Name', value: 'TeacherLastName' },
      { label: 'Teacher First Name', value: 'TeacherFirstName' },
      { label: 'Project ID', value: 'ProjectId' },
      { label: 'Grade', value: 'Grade' },

      //decending options
      { label: 'Last Name (Z-A)', value: 'LastName desc' },
      { label: 'First Name (Z-A)', value: 'FirstName desc' },
      { label: 'Entry Title (Z-A)', value: 'Title desc' },
      { label: 'Category (Z-A)', value: 'Category_name desc' },
      { label: 'School (Z-A)', value: 'School desc' },
      { label: 'Teacher Last Name (Z-A)', value: 'TeacherLastName desc' },
      { label: 'Teacher First Name (Z-A)', value: 'TeacherFirstName desc' },
      { label: 'Project ID (Z-A)', value: 'ProjectId desc' },
      { label: 'Grade (Z-A)', value: 'Grade desc' },
    ];
    setOrderByOptions(orderBys);
  }, [info]);

  const init = () => {
    //call server get info
    store.server.getApi<ServerResponse<IArchiveSearchInfo>>(`../ArchiveSearcher/Info`).then((x) => {
      if (x.Success) {
        setInfo(x.Value);
      } else {
        toast.error(x.Message, { autoClose: false });
      }
    });
  };

  const RunSearch = () => {
    //make sure there is at least one year selected
    if (!search.events || search.events.length === 0) {
      toast.error('Please select at least one year to search.');
      return;
    }

    //make sure some other search criteria is selected
    if (
      !search.firstName &&
      !search.lastName &&
      !search.projectTitle &&
      !search.school &&
      !search.category &&
      !search.award &&
      !search.specialAward &&
      !search.projectId &&
      !search.teacherFirstName &&
      !search.teacherLastName
    ) {
      toast.error('Please enter at least one other search criteria.');
      return;
    }

    store.server.postApi<ServerResponse<IArchiveSearchResult>>(`../ArchiveSearcher/Search`, search).then((x) => {
      if (x.Success) {
        setData(x.Value);
        //how many records found...
        if (x.Value.data.length === 0) {
          toast.info('No records found matching your search criteria.');
        } else {
          toast.success(`${x.Value.data.length} record(s) found matching your search criteria.`);
          setShowSearchModal(false);
        }
      } else {
        toast.error(x.Message, { autoClose: false });
      }
    });
  };

  return (
    <>
      <div>
        <div className="flex-between">
          <div>
            <h2>Archive Search</h2>
          </div>
          <div>
            <Button type="edit" text="New Search" onClick={() => setShowSearchModal(true)} />
          </div>
        </div>

        <div>
          {data && data.data.length > 0 && (
            <table className="table table-bordered table-striped table-hover">
              <thead>
                <tr>
                  <th>Year</th>
                  <th>Name</th>
                  <th>Entry Title</th>
                  <th>Category</th>
                  <th>School</th>
                  <th>Teacher</th>
                  <th>Award(s)</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {data.data.map((row, i) => (
                  <tr key={i}>
                    <td className="result-data-year">
                      {row.DataSetYear}
                      <span className="small hide">
                        Id: {row.DataSetYear}-{row.DataSetId}
                      </span>
                    </td>
                    <td>
                      {row.FirstName} {row.LastName}
                    </td>
                    <td>
                      <div className="flex-between">
                        <div>{row.ProjectId ?? ''}</div>
                        <div>
                          {row.IsTeam && (
                            <>
                              <span className="label label-default small">Team</span>
                            </>
                          )}
                        </div>
                      </div>
                      {row.Title && <div dangerouslySetInnerHTML={{ __html: row.Title }} />}
                    </td>
                    <td>{row.Category_name}</td>
                    <td>{row.School}</td>
                    <td>
                      {row.TeacherFirstName} {row.TeacherLastName}
                    </td>
                    <td className=''>
                      {row.Award && row.Award.trim().length > 1 && <div><Icon type={IconType.trophy}/> "{row.Award}"</div>}
                      {row.SpecialAwards && row.SpecialAwards.length > 0 && row.SpecialAwards.map((sa, idx) => (
                        <div key={idx}><Icon type={IconType.award}/> {sa}</div>
                      ))}
                    </td>
                    <td>
                      <Button
                        type="details"
                        text="View Details"
                        onClick={() => {
                          setSelectedResult(row);
                          setShowSelectedResult(true);
                        }}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {data && data.data.length === 0 && <div className="alert alert-info">No results found.</div>}
        </div>
      </div>

      {selectedResult && showSelectedResult && (
        <Modal size="xl" title="Record Details" setModalOpen={setShowSelectedResult}>
          <Tabs>
            <TabList>
              <Tab>Person</Tab>
              {showResultsTabSchool && <Tab>School</Tab>}
              {showResultsTabProject && <Tab>Project</Tab>}
              {showResultsTabAwards && <Tab>Awards</Tab>}
            </TabList>
            <TabPanel>
              <div className="form-horizontal">
                {(selectedResult.FirstName || selectedResult.LastName) && (
                  <div className="form-group">
                    <label className="col-sm-2 control-label">Name</label>
                    <div className="col-sm-10 form-control-static">
                      {selectedResult.FirstName} {selectedResult.LastName}
                    </div>
                  </div>
                )}
                {selectedResult.Email && (
                  <div className="form-group">
                    <label className="col-sm-2 control-label">Email</label>
                    <div className="col-sm-10 form-control-static">{selectedResult.Email}</div>
                  </div>
                )}
                {selectedResult.Phone && (
                  <div className="form-group">
                    <label className="col-sm-2 control-label">Phone</label>
                    <div className="col-sm-10 form-control-static">{FormatPhoneNumber(selectedResult.Phone)}</div>
                  </div>
                )}
                {selectedResult.CellPhone && (
                  <div className="form-group">
                    <label className="col-sm-2 control-label">Cell Phone</label>
                    <div className="col-sm-10 form-control-static">{FormatPhoneNumber(selectedResult.CellPhone)}</div>
                  </div>
                )}
                {selectedResult.ParentEmail && (
                  <div className="form-group">
                    <label className="col-sm-2 control-label">Parent Email</label>
                    <div className="col-sm-10 form-control-static">{selectedResult.ParentEmail}</div>
                  </div>
                )}
                {(selectedResult.Street1 || selectedResult.Street2 || selectedResult.City || selectedResult.State || selectedResult.Zip) && (
                  <div className="form-group">
                    <label className="col-sm-2 control-label">Address</label>
                    <div className="col-sm-10 form-control-static">
                      {selectedResult.Street1}
                      {selectedResult.Street2 && (
                        <>
                          <br />
                          {selectedResult.Street2}
                        </>
                      )}
                      {(selectedResult.City || selectedResult.State || selectedResult.Zip) && (
                        <>
                          <br />
                          {selectedResult.City}
                          {selectedResult.City && (selectedResult.State || selectedResult.Zip) ? ', ' : ''}
                          {selectedResult.State} {selectedResult.Zip}
                        </>
                      )}
                    </div>
                  </div>
                )}
                <div className="form-group">
                  <label className="col-sm-2 control-label">Data Key</label>
                  <div className="col-sm-10 form-control-static">
                    Year: {selectedResult.DataSetYear} Id: {selectedResult.DataSetId}
                  </div>
                </div>
              </div>
            </TabPanel>
            {showResultsTabSchool && (
              <TabPanel>
                <div className="form-horizontal">
                  {selectedResult.School && (
                    <div className="form-group">
                      <label className="col-sm-2 control-label">School</label>
                      <div className="col-sm-10 form-control-static">{selectedResult.School}</div>
                    </div>
                  )}
                  {(selectedResult.TeacherFirstName || selectedResult.TeacherLastName) && (
                    <div className="form-group">
                      <label className="col-sm-2 control-label">Teacher</label>
                      <div className="col-sm-10 form-control-static">
                        {selectedResult.TeacherFirstName} {selectedResult.TeacherLastName}
                      </div>
                    </div>
                  )}
                  {selectedResult.Grade !== undefined && selectedResult.Grade !== null && (
                    <div className="form-group">
                      <label className="col-sm-2 control-label">Grade</label>
                      <div className="col-sm-10 form-control-static">{selectedResult.Grade}</div>
                    </div>
                  )}
                </div>
              </TabPanel>
            )}
            {showResultsTabProject && (
              <TabPanel>
                <div className="form-horizontal">
                  {selectedResult.IsTeam !== undefined && (
                    <div className="form-group">
                      <label className="col-sm-3 control-label">Team Entry</label>
                      <div className="col-sm-9 form-control-static">{selectedResult.IsTeam ? 'Yes' : 'No'}</div>
                    </div>
                  )}
                  {selectedResult.TeamMembers && (
                    <div className="form-group">
                      <label className="col-sm-3 control-label">Team Members</label>
                      <div className="col-sm-9 form-control-static">{selectedResult.TeamMembers}</div>
                    </div>
                  )}
                  {selectedResult.Title && (
                    <div className="form-group">
                      <label className="col-sm-3 control-label">Title</label>
                      <div className="col-sm-9 form-control-static">
                        <div dangerouslySetInnerHTML={{ __html: selectedResult.Title }} />
                      </div>
                    </div>
                  )}
                  {selectedResult.Category_name && (
                    <div className="form-group">
                      <label className="col-sm-3 control-label">Category</label>
                      <div className="col-sm-9 form-control-static">{selectedResult.Category_name}</div>
                    </div>
                  )}
                  {selectedResult.ProjectId && (
                    <div className="form-group">
                      <label className="col-sm-3 control-label">Project ID</label>
                      <div className="col-sm-9 form-control-static">{selectedResult.ProjectId}</div>
                    </div>
                  )}
                  {selectedResult.ProjectTagsCsv && (
                    <div className="form-group">
                      <label className="col-sm-3 control-label">Tags</label>
                      <div className="col-sm-9 form-control-static">{selectedResult.ProjectTagsCsv}</div>
                    </div>
                  )}
                  {selectedResult.ProjectDescription && (
                    <div className="form-group">
                      <label className="col-sm-3 control-label">Description</label>
                      <div className="col-sm-9 form-control-static">
                        <div dangerouslySetInnerHTML={{ __html: selectedResult.ProjectDescription }} />
                      </div>
                    </div>
                  )}
                  {selectedResult.EntryTypeName && (
                    <div className="form-group">
                      <label className="col-sm-3 control-label">Entry Type</label>
                      <div className="col-sm-9 form-control-static">{selectedResult.EntryTypeName}</div>
                    </div>
                  )}
                  {selectedResult.ProjectStatus && (
                    <div className="form-group">
                      <label className="col-sm-3 control-label">Project Status</label>
                      <div className="col-sm-9 form-control-static">{selectedResult.ProjectStatus}</div>
                    </div>
                  )}
                  {selectedResult.ProjectPlan && (
                    <div className="form-group">
                      <label className="col-sm-3 control-label">Project Plan</label>
                      <div className="col-sm-9 form-control-static">
                        <div dangerouslySetInnerHTML={{ __html: selectedResult.ProjectPlan }} />
                      </div>
                    </div>
                  )}
                  {selectedResult.ProjectAbstract && (
                    <div className="form-group">
                      <label className="col-sm-3 control-label">Project Abstract</label>
                      <div className="col-sm-9 form-control-static">
                        <div dangerouslySetInnerHTML={{ __html: selectedResult.ProjectAbstract }} />
                      </div>
                    </div>
                  )}
                </div>
              </TabPanel>
            )}
            {showResultsTabAwards && (
              <TabPanel>
                <div className="form-horizontal">
                  {selectedResult.Award && (
                    <div className="form-group">
                      <label className="col-sm-3 control-label">Award <Icon type={IconType.trophy} /></label>
                      <div className="col-sm-9 form-control-static">{selectedResult.Award}</div>
                    </div>
                  )}
                  {selectedResult.SpecialAwards && selectedResult.SpecialAwards.length > 0 && (
                    <div className="form-group">
                      <label className="col-sm-3 control-label">Special Awards <Icon type={IconType.award} /></label>
                      <div className="col-sm-9 form-control-static">
                        {selectedResult.SpecialAwards.map((sa, idx) => (
                          <div key={idx}>{sa}</div>
                        ))}
                      </div>
                    </div>
                  )}
                  {/* {selectedResult.SpecialAward && (
                    <div className="form-group">
                      <label className="col-sm-3 control-label">Special Award</label>
                      <div className="col-sm-9 form-control-static">{selectedResult.SpecialAward}</div>
                    </div>
                  )}
                  {selectedResult.SpecialAwardSubType && (
                    <div className="form-group">
                      <label className="col-sm-3 control-label">Special Award SubType</label>
                      <div className="col-sm-9 form-control-static">{selectedResult.SpecialAwardSubType}</div>
                    </div>
                  )} */}
                </div>
              </TabPanel>
            )}
          </Tabs>
        </Modal>
      )}

      {info && showSearchModal && (
        <Modal size="l" title="New Search" setModalOpen={setShowSearchModal}>
          <div className="form-horizontal">
            <h3>Order By Criteria</h3>
            <p>Select the criteria by which to order the search results.</p>
            <div className="form-group">
              <label className="col-sm-3 control-label" htmlFor="orderBy">
                Order By
              </label>
              <Select
                inputId="orderBy"
                className="col-sm-8"
                options={orderByOptions}
                isMulti={true}
                value={orderByOptions.filter((option) => search.orderBy?.includes(option.value as string))}
                onChange={(selected) => setSearch({ ...search, orderBy: selected.map((s) => s.value as string) })}
                placeholder="Select Order By Criteria..."
              />
            </div>
            <br />
            <div className="clearfix"></div>
            <h3>Search Criteria</h3>
            <p>Please specify your search criteria below:</p>
            <div className="form-group">
              <label className="col-sm-3 control-label" htmlFor="years">
                Select Years
              </label>
              <Select
                inputId="years"
                className="col-sm-8"
                options={archiveYears}
                isMulti={true}
                value={archiveYears.filter((option) => search.events?.includes(+option.value))}
                onChange={(selected) => setSearch({ ...search, events: selected.map((s) => s.value as number) })}
                placeholder="Select Search Years..."
              />
            </div>
            <div className="form-group">
              <label className="col-sm-3 control-label" htmlFor="grades">
                Division or Grades
              </label>
              <Select
                inputId="grades"
                className="col-sm-8"
                options={grades}
                isMulti={true}
                value={grades.filter((option) => search.grades?.includes(+option.value))}
                onChange={(selected) => setSearch({ ...search, grades: selected.map((s) => s.value as number) })}
                placeholder="Select Search Division or Grades..."
              />
            </div>
            <div className="form-group">
              <label className="col-sm-3 control-label" htmlFor="firstName">
                First Name
              </label>
              <div className="col-sm-3">
                <select
                  id="firstNameOperator"
                  value={search.firstNameOperator}
                  onChange={(e) => setSearch({ ...search, firstNameOperator: e.target.value as ArchiveResultsSearchOperator })}
                  className="form-control">
                  {stringOperators.map((op) => (
                    <option key={op.value} value={op.value}>
                      {op.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-sm-5">
                <input
                  id="firstName"
                  type="text"
                  value={search.firstName}
                  onChange={(e) => setSearch({ ...search, firstName: e.target.value })}
                  placeholder="Enter First Name..."
                  maxLength={25}
                  className="form-control"
                />
              </div>
            </div>
            <div className="form-group">
              <label className="col-sm-3 control-label" htmlFor="lastName">
                Last Name
              </label>
              <div className="col-sm-3">
                <select
                  id="lastNameOperator"
                  value={search.lastNameOperator}
                  onChange={(e) => setSearch({ ...search, lastNameOperator: e.target.value as ArchiveResultsSearchOperator })}
                  className="form-control">
                  {stringOperators.map((op) => (
                    <option key={op.value} value={op.value}>
                      {op.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-sm-5">
                <input
                  id="lastName"
                  type="text"
                  value={search.lastName}
                  onChange={(e) => setSearch({ ...search, lastName: e.target.value })}
                  placeholder="Enter Last Name..."
                  maxLength={25}
                  className="form-control"
                />
              </div>
            </div>
            <div className="form-group">
              <label className="col-sm-3 control-label" htmlFor="projectTitle">
                Entry Title
              </label>
              <div className="col-sm-3">
                <select
                  id="projectTitleOperator"
                  value={search.projectTitleOperator}
                  onChange={(e) => setSearch({ ...search, projectTitleOperator: e.target.value as ArchiveResultsSearchOperator })}
                  className="form-control">
                  {stringOperators.map((op) => (
                    <option key={op.value} value={op.value}>
                      {op.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-sm-5">
                <input
                  id="projectTitle"
                  type="text"
                  value={search.projectTitle}
                  onChange={(e) => setSearch({ ...search, projectTitle: e.target.value })}
                  placeholder="Enter Entry Title..."
                  maxLength={250}
                  className="form-control"
                />
              </div>
            </div>
            <div className="form-group">
              <label className="col-sm-3 control-label" htmlFor="projectId">
                Entry Id
              </label>
              <div className="col-sm-3">
                <select
                  id="projectIdOperator"
                  value={search.projectIdOperator}
                  onChange={(e) => setSearch({ ...search, projectIdOperator: e.target.value as ArchiveResultsSearchOperator })}
                  className="form-control">
                  {stringOperators.map((op) => (
                    <option key={op.value} value={op.value}>
                      {op.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-sm-5">
                <input
                  id="projectId"
                  type="text"
                  value={search.projectId}
                  onChange={(e) => setSearch({ ...search, projectId: e.target.value })}
                  placeholder="Enter Entry Id..."
                  maxLength={250}
                  className="form-control"
                />
              </div>
            </div>
            <div className="form-group">
              <label className="col-sm-3 control-label" htmlFor="school">
                School
              </label>
              <div className="col-sm-3">
                <select
                  id="schoolOperator"
                  value={search.schoolOperator}
                  onChange={(e) => setSearch({ ...search, schoolOperator: e.target.value as ArchiveResultsSearchOperator })}
                  className="form-control">
                  {stringOperators.map((op) => (
                    <option key={op.value} value={op.value}>
                      {op.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-sm-5">
                <input
                  id="school"
                  type="text"
                  value={search.school}
                  onChange={(e) => setSearch({ ...search, school: e.target.value })}
                  placeholder="Enter School..."
                  maxLength={50}
                  className="form-control"
                />
              </div>
            </div>
            <div className="form-group">
              <label className="col-sm-3 control-label" htmlFor="category">
                Category
              </label>
              <div className="col-sm-3">
                <select
                  id="categoryOperator"
                  value={search.categoryOperator}
                  onChange={(e) => setSearch({ ...search, categoryOperator: e.target.value as ArchiveResultsSearchOperator })}
                  className="form-control">
                  {stringOperators.map((op) => (
                    <option key={op.value} value={op.value}>
                      {op.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-sm-5">
                <input
                  id="category"
                  type="text"
                  value={search.category}
                  onChange={(e) => setSearch({ ...search, category: e.target.value })}
                  placeholder="Enter Category..."
                  maxLength={100}
                  className="form-control"
                />
              </div>
            </div>
            <div className="form-group">
              <label className="col-sm-3 control-label" htmlFor="award">
                Category Award
              </label>
              <div className="col-sm-3">
                <select
                  id="awardOperator"
                  value={search.awardOperator}
                  onChange={(e) => setSearch({ ...search, awardOperator: e.target.value as ArchiveResultsSearchOperator })}
                  className="form-control">
                  {stringOperators.map((op) => (
                    <option key={op.value} value={op.value}>
                      {op.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-sm-5">
                <input
                  id="award"
                  type="text"
                  value={search.award}
                  onChange={(e) => setSearch({ ...search, award: e.target.value })}
                  placeholder="Enter Award..."
                  maxLength={100}
                  className="form-control"
                />
              </div>
            </div>
            <div className="form-group">
              <label className="col-sm-3 control-label" htmlFor="specialAward">
                Special Awards
              </label>
              <div className="col-sm-3">
                <select
                  id="specialAwardOperator"
                  value={search.specialAwardOperator}
                  onChange={(e) => setSearch({ ...search, specialAwardOperator: e.target.value as ArchiveResultsSearchOperator })}
                  className="form-control">
                  {stringOperators.map((op) => (
                    <option key={op.value} value={op.value}>
                      {op.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-sm-5">
                <input
                  id="specialAward"
                  type="text"
                  value={search.specialAward}
                  onChange={(e) => setSearch({ ...search, specialAward: e.target.value })}
                  placeholder="Enter Special Award..."
                  maxLength={100}
                  className="form-control"
                />
              </div>
            </div>
            <div className="form-group">
              <label className="col-sm-3 control-label" htmlFor="teacherFirstName">
                Teacher First Name
              </label>
              <div className="col-sm-3">
                <select
                  id="teacherFirstNameOperator"
                  value={search.teacherFirstNameOperator}
                  onChange={(e) => setSearch({ ...search, teacherFirstNameOperator: e.target.value as ArchiveResultsSearchOperator })}
                  className="form-control">
                  {stringOperators.map((op) => (
                    <option key={op.value} value={op.value}>
                      {op.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-sm-5">
                <input
                  id="teacherFirstName"
                  type="text"
                  value={search.teacherFirstName}
                  onChange={(e) => setSearch({ ...search, teacherFirstName: e.target.value })}
                  placeholder="Enter Teacher First Name..."
                  maxLength={100}
                  className="form-control"
                />
              </div>
            </div>
            <div className="form-group">
              <label className="col-sm-3 control-label" htmlFor="teacherLastName">
                Teacher Last Name
              </label>
              <div className="col-sm-3">
                <select
                  id="teacherLastNameOperator"
                  value={search.teacherLastNameOperator}
                  onChange={(e) => setSearch({ ...search, teacherLastNameOperator: e.target.value as ArchiveResultsSearchOperator })}
                  className="form-control">
                  {stringOperators.map((op) => (
                    <option key={op.value} value={op.value}>
                      {op.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-sm-5">
                <input
                  id="teacherLastName"
                  type="text"
                  value={search.teacherLastName}
                  onChange={(e) => setSearch({ ...search, teacherLastName: e.target.value })}
                  placeholder="Enter Teacher Last Name..."
                  maxLength={100}
                  className="form-control"
                />
              </div>
            </div>

            <div className="form-group">
              <br />
              <br />
            </div>
            <div className="form-group">
              <div className="col-sm-offset-3 col-sm-8">
                <button className="btn btn-secondary" onClick={() => RunSearch()}>
                  <i className="fa fa-search"></i>
                  Run Search
                </button>
              </div>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};

export default ReduxMap(ArchiveSearch);
